import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/frontPage.css';
import '../styles/reviewsPage.css';
import Seo from '../seo/Seo';
import { breadcrumb, webPage } from '../seo/structuredData';
import SearchableSelect from '../components/SearchableSelect';
import {
  HOMEOWNER_REVIEWS,
  reviewStats,
  roundedStars,
  type HomeownerReview,
} from '../data/homeownerReviews';

/** The page's meta description, shared with its WebPage structured data. */
const REVIEWS_DESCRIPTION =
  'Reviews from Phoenix-area homeowners who posted a project on Nestimates, published verbatim with their first name, city and month, and our reply to each.';

/**
 * ReviewsPage
 * ───────────
 * Every review, in full, transcribed from the hand-built reviews page
 * (`nestimates-reviews.html`): same sections, same copy, same stylesheet.
 *
 * The homepage shows three of these cut at 240 characters; this is where
 * "Read the full review" lands, so each `<article>` carries the review's id and
 * `/reviews#larry-n` scrolls straight to it. App-wide `ScrollToTop` already
 * does that scrolling (App.tsx:280) — it polls for the target, so it copes with
 * this chunk still loading. All this page owes it is a stable id and enough
 * `scroll-margin-top` to clear the navbar.
 *
 * Two deviations from the source file, both for the same reason — this is a
 * page inside an app, not a standalone document:
 *
 *  - its <header> and <footer> are dropped, because the app renders its own
 *    Navbar and LegalFooter around every route;
 *  - `/post` becomes the real intake at `/`, which is where posting a project
 *    actually starts here.
 *
 * The reviews themselves are NOT in this file. They live in
 * `data/homeownerReviews`, which the homepage reads too — the source file's own
 * note asks for exactly that ("one source of truth"), and two copies of
 * thirteen verbatim quotes is two copies to get out of step.
 */

/** The Nestimates mark, on the response block. */
const Mark: React.FC = () => (
  <svg viewBox="0 0 100 100" aria-hidden="true">
    <path
      d="M35 69V37l30 26v8"
      fill="none"
      stroke="#F26522"
      strokeWidth="11"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M60 39c0-6 4-10 9-10s9 4 9 10v13c0 4-3 6-6 3L61 45c-1-1-1-4-1-6z" fill="#1B2A4A" />
  </svg>
);

const Stars: React.FC<{ stars: number; className?: string }> = ({ stars, className }) => {
  const filled = roundedStars(stars);
  return (
    <span className={className ?? 'stars'} aria-label={`${stars} out of 5 stars`}>
      {'★'.repeat(filled) + '☆'.repeat(5 - filled)}
    </span>
  );
};

const ReviewCard: React.FC<{ review: HomeownerReview }> = ({ review }) => (
  <article className="rv" id={review.id}>
    <div className="rv-top">
      <div>
        <Stars stars={review.stars} />
        <div className="rv-proj">{review.project}</div>
      </div>
      <div className="rv-date">{review.date}</div>
    </div>
    <p className="q">{review.quote}</p>
    <div className="rv-who">
      <div className="av" aria-hidden="true">
        {review.name.charAt(0)}
      </div>
      <div>
        <div className="nm">{review.name}</div>
        <div className="meta">
          {review.city}. {review.source}.
        </div>
      </div>
    </div>
    {review.response && (
      <div className="rv-resp">
        <div className="from">
          <Mark />
          Response from Nestimates
        </div>
        <p>{review.response}</p>
      </div>
    )}
  </article>
);

const ReviewsPage: React.FC = () => {
  const [projectFilter, setProjectFilter] = useState('');

  const stats = useMemo(() => reviewStats(), []);

  /**
   * Every project type that actually has a review, A–Z, each carrying its own
   * count — so the list says what is behind a choice before you make it.
   * "All project types" is a real option rather than a cleared state, because
   * a filter you cannot undo from inside the control is a trap.
   */
  const projectOptions = useMemo(() => {
    const counts = new Map<string, number>();
    HOMEOWNER_REVIEWS.forEach((review) => {
      counts.set(review.project, (counts.get(review.project) ?? 0) + 1);
    });
    const say = (n: number) => `${n} ${n === 1 ? 'review' : 'reviews'}`;
    return [
      { value: '', label: 'All project types', hint: say(HOMEOWNER_REVIEWS.length) },
      ...Array.from(counts.keys())
        .sort()
        .map((project) => ({ value: project, label: project, hint: say(counts.get(project) ?? 0) })),
    ];
  }, []);

  const shown = useMemo(
    () =>
      projectFilter
        ? HOMEOWNER_REVIEWS.filter((review) => review.project === projectFilter)
        : HOMEOWNER_REVIEWS,
    [projectFilter]
  );

  return (
    <div className="nx-front">
      <Seo
        title="Homeowner Reviews"
        description={REVIEWS_DESCRIPTION}
        path="/reviews"
        // The page as a collection, and deliberately nothing about the reviews
        // inside it: no Review and no AggregateRating markup until where these
        // reviews came from is confirmed. Rating markup is what Google polices
        // hardest, and titleGate.test.ts fails on either type anywhere.
        jsonLd={[
          webPage({
            type: 'CollectionPage',
            path: '/reviews',
            name: 'Homeowner Reviews',
            description: REVIEWS_DESCRIPTION,
          }),
          breadcrumb([['Reviews', '/reviews']]),
        ]}
      />

      <section className="rv-hero" id="top">
        <div className="shell">
          <h1>Homeowner reviews</h1>
          <p className="rv-lede">
            Every review here is from a homeowner who posted a project on Nestimates, in their own
            words, and every one gets an answer.
          </p>

          <div className="rv-sum">
            <div className="rv-score">
              <div>
                {/* Computed from the reviews below, never typed in, so the
                    headline number cannot drift from what it averages. */}
                <div className="big">{stats.average.toFixed(2)}</div>
              </div>
              <div>
                <Stars stars={stats.average} />
                <div className="n">{`out of 5, from ${stats.count} homeowner reviews`}</div>
              </div>
            </div>
            <div className="rv-how">
              <b>How we collect reviews.</b> We ask every homeowner for a review after their project
              is complete. Reviews are published verbatim, including the parts that aren't
              flattering, with the homeowner's first name, city, and month. We don't pay for reviews
              and we don't remove critical ones. The project type shown is the Nestimates job type
              closest to what the homeowner wrote.
            </div>
          </div>

          <div className="rv-tools">
            {/* The label and the control are ONE child of this row, which is
                spaced `space-between` against the count on the right. As two
                children they were spread apart and the control floated into the
                middle of the row.

                A <label for> would name the trigger "Project type" and swallow
                the current choice, so the label is plain text and `labelledBy`
                names the control "Project type, <choice>" instead. That is why
                this is a <div> and not the <label> the source file used. */}
            <div className="rv-filter">
              <span className="rv-filter-label" id="rv-filter-label">
                Project type
              </span>
              <SearchableSelect
                id="rv-filter"
                className="rv-select"
                labelledBy="rv-filter-label"
                value={projectFilter}
                onChange={setProjectFilter}
                options={projectOptions}
                searchLabel="Search project types"
                searchPlaceholder="Search project types…"
              />
            </div>
            {/* Announced, because changing the filter changes this number and
                nothing else a screen reader would notice. */}
            <span className="rv-count" role="status">
              {`${shown.length} ${shown.length === 1 ? 'review' : 'reviews'}`}
            </span>
          </div>
        </div>
      </section>

      <section className="band rv-band">
        <div className="shell">
          {shown.length ? (
            <div className="rv-list">
              {shown.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          ) : (
            <div className="rv-empty">No reviews for that project type yet.</div>
          )}
        </div>
      </section>

      <section className="band">
        <div className="shell close-in">
          <h2>Post it once. Let the bids come to you.</h2>
          <div className="close-cta">
            <Link className="btn btn-orange" to="/">
              Post your project
            </Link>
          </div>
          <p className="close-fine">
            Had a project done through Nestimates?{' '}
            <a href="mailto:hello@nestimates.com?subject=My%20Nestimates%20review">
              Send us your review
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
